import React, { useState } from 'react';
import type { UserProfile, UserRole } from '@types';
import { adminAssignmentError, type AdminRole, type AdminFunctionalRole, type Region } from '../common/adminAccess';
import AdminAssignmentFields from './AdminAssignmentFields';
import { USER_ROLES, USER_ROLE_LABELS, USER_STATUSES, USER_STATUS_LABELS } from '../utils/userDisplayLabels';

export interface PlatformUserUpdate {
    displayName: string;
    firstName: string;
    lastName: string;
    title: string;
    phone: string;
    role: UserRole;
    status: typeof USER_STATUSES[number];
    adminRole: Exclude<AdminRole, 'Super'> | '';
    adminRoles: AdminFunctionalRole[];
    regions: Region[];
}

interface Props {
    user: UserProfile;
    busy: boolean;
    canPromote?: boolean;
    onClose: () => void;
    onSave: (update: PlatformUserUpdate) => Promise<void>;
}

const PlatformUserEditorModal: React.FC<Props> = ({ user, busy, canPromote = false, onClose, onSave }) => {
    const [form, setForm] = useState<PlatformUserUpdate>({
        displayName: user.displayName || '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        title: user.title || '',
        phone: user.phone || '',
        role: user.role,
        status: user.status === 'deleted' ? 'suspended' : user.status,
        adminRole: '',
        adminRoles: [],
        regions: [],
    });
    const [error, setError] = useState('');

    const submit = async (event: React.FormEvent) => {
        event.preventDefault();
        const assignmentError = form.adminRole ? adminAssignmentError(form.adminRoles, form.regions) : null;
        if (assignmentError) {
            setError(assignmentError);
            return;
        }
        setError('');
        await onSave(form);
    };

    return (
        <div className="fixed inset-0 z-[130] flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
            <form onSubmit={submit} className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
                <div className="mb-5 flex items-center justify-between">
                    <h2 className="text-xl font-extrabold text-slate-900">Edit User</h2>
                    <button type="button" onClick={onClose} className="rounded-lg px-3 py-1 text-xl text-slate-400 hover:bg-slate-100 hover:text-slate-700">X</button>
                </div>
                {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{error}</div>}
                <div className="space-y-4">
                    <label className="block text-xs font-bold uppercase text-slate-500">Name<input required value={form.displayName} onChange={event => setForm({ ...form, displayName: event.target.value })} className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal normal-case" /></label>
                    <div className="grid gap-4 md:grid-cols-2">
                        <label className="block text-xs font-bold uppercase text-slate-500">First Name<input value={form.firstName} onChange={event => setForm({ ...form, firstName: event.target.value })} className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal normal-case" /></label>
                        <label className="block text-xs font-bold uppercase text-slate-500">Last Name<input value={form.lastName} onChange={event => setForm({ ...form, lastName: event.target.value })} className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal normal-case" /></label>
                        <label className="block text-xs font-bold uppercase text-slate-500">Job Title<input value={form.title} onChange={event => setForm({ ...form, title: event.target.value })} className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal normal-case" /></label>
                        <label className="block text-xs font-bold uppercase text-slate-500">Phone<input type="tel" value={form.phone} onChange={event => setForm({ ...form, phone: event.target.value })} className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal normal-case" /></label>
                    </div>
                    <label className="block text-xs font-bold uppercase text-slate-500">Email<input disabled value={user.email} className="mt-1 block w-full rounded-lg border border-slate-200 bg-slate-100 px-3 py-2 text-sm font-normal normal-case text-slate-500" /></label>
                    <div className="grid gap-4 md:grid-cols-2">
                        <label className="block text-xs font-bold uppercase text-slate-500">Role<select value={form.role} disabled={user.role === 'owner'} onChange={event => setForm({ ...form, role: event.target.value as UserRole })} className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-normal normal-case disabled:bg-slate-100">{USER_ROLES.filter(role => role !== 'owner' || user.role === 'owner').map(role => <option key={role} value={role}>{USER_ROLE_LABELS[role]}</option>)}</select></label>
                        <label className="block text-xs font-bold uppercase text-slate-500">Status<select value={form.status} onChange={event => setForm({ ...form, status: event.target.value as PlatformUserUpdate['status'] })} className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-normal normal-case">{USER_STATUSES.map(status => <option key={status} value={status}>{USER_STATUS_LABELS[status]}</option>)}</select></label>
                    </div>
                    {canPromote && (
                        <label className="block text-xs font-bold uppercase text-slate-500">Admin Level<select value={form.adminRole} onChange={event => setForm({ ...form, adminRole: event.target.value as PlatformUserUpdate['adminRole'] })} className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-normal normal-case"><option value="">No administrator access</option><option value="Global">Global Admin</option><option value="Regional">Regional Admin</option></select></label>
                    )}
                    {canPromote && form.adminRole && <AdminAssignmentFields adminRoles={form.adminRoles} regions={form.regions} onChange={(adminRoles, regions) => setForm({ ...form, adminRoles, regions })} />}
                </div>
                <button disabled={busy} className="mt-6 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-50">{busy ? 'Saving...' : form.adminRole ? 'Save And Promote' : 'Save User'}</button>
            </form>
        </div>
    );
};

export default PlatformUserEditorModal;
